import { useEffect, useState } from "react"
import { Ticker } from "motion-plus/react"
import { motion } from "motion/react"
import { getBlueFoxMasterSanityData } from "services/MovieApi"
import type { IMovie } from "interfaces/IMovie"

type ReleaseScope = "domestic" | "international" | "both"

function PosterBox({ movie }: { movie: IMovie }) {
  const title = movie.title ?? ""
  const src = movie.poster_path ?? ""
  const href = `/movie/detail/${encodeURIComponent(movie.slug || "")}`

  return (
    <a href={href} aria-label={`Open ${title}`} className="block">
      <motion.div className="bfe-item" initial="hideInfo" whileHover="showInfo">
        <motion.img
          src={src}
          alt={`${title} poster`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover block"
          variants={{
            hideInfo: { scale: 1, filter: "blur(0px)", opacity: 1 },
            showInfo: { scale: 0.95, filter: "blur(4px)", opacity: 0.35 },
          }}
        />
        <motion.div
          className="bfe-title"
          variants={{
            hideInfo: { opacity: 0, scale: 1.06 },
            showInfo: { opacity: 1, scale: 1 },
          }}
        >
          {title}
        </motion.div>
      </motion.div>
      {/* caption (year) under the card, optional) */}
      <div className="mt-2 text-xs text-slate-400 text-center">
        {movie.release_date?.slice(0, 4)}
      </div>
    </a>
  )
}

export default function FestivalTickerStrip({
                                          scope = "international",
                                          // speed = 1,            // lower = slower, higher = faster
                                          hoverFactor = 0       // 0 = don't react to cursor speed
                                        }: {
  scope?: ReleaseScope
  speed?: number
  hoverFactor?: number
}) {
  const [movies, setMovies] = useState<IMovie[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data } = await getBlueFoxMasterSanityData(scope)
        const list: IMovie[] = (data?.results || data?.items || [])
          .filter((m: any) => m?.poster_path && m?.slug)
        if (alive) setMovies(list)
      } catch (e: any) {
        if (alive) setErr(e?.message || "Failed to load movies")
      }
    })()
    return () => { alive = false }
  }, [scope])

  return (
    <section className="mt-6">
      <h3 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-slate-50 tracking-wide">

      </h3>

      <div className="mt-4 rounded-2xl border border-slate-200/20 dark:border-slate-800/60 p-3">
        {/* states */}
        {err && <p className="text-sm text-red-500">{err}</p>}
        {!movies && !err && (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
        {movies && movies.length === 0 && (
          <p className="text-sm text-slate-500">No movies yet.</p>
        )}

        {movies && movies.length > 0 && (
          <>
            <div className="bfe-ticker-wrap">
              <Ticker
                // speed={speed}
                hoverFactor={hoverFactor}
                items={movies.map((m) => (
                  <PosterBox key={m.id ?? m.slug} movie={m} />
                ))}
                style={{
                  maskImage:
                    "linear-gradient(to right, transparent 5%, black 10%, black 90%, transparent 95%)",
                }}
              />
            </div>
            <Styles />
          </>
        )}
      </div>
    </section>
  )
}

/** minimal CSS for consistent sizing + overlay text */
function Styles() {
  return (
    <style>{`
      .bfe-ticker-wrap { overflow: hidden; }
      .bfe-item {
        width: 180px;
        height: 270px;
        position: relative;
        overflow: hidden;
        border-radius: 16px;
        box-shadow: 0 4px 30px rgba(0,0,0,0.25);
        background: #0b1220;
      }
      @media (min-width: 1024px) {
        .bfe-item { width: 220px; height: 330px; }
      }
      .bfe-title {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        color: #f5f5f5;
        font-weight: 700;
        font-size: 14px;
        text-align: center;
        padding: 0 8px;
        text-shadow: 0 1px 0 #000;
        pointer-events: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .bfe-item img { filter: none !important }
      }
    `}</style>
  )
}
